import React from "react";
import { Link } from "react-router-dom";
import type { AppLanguage } from "locales";

/**
 * Privacy Policy content per language.
 *
 * Stored as full JSX trees (not strings) so that <strong>, <ul>, <a>, and
 * <Link> stay first-class — no placeholder gymnastics. The page component
 * picks the tree by current language and renders verbatim.
 */
export interface LegalContent {
  title: string;
  lastUpdated: string;
  body: React.ReactNode;
}

const en: LegalContent = {
  title: "Privacy Policy",
  lastUpdated: "Last updated: April 10, 2026",
  body: (
    <>
      <section>
        <h2>1. Introduction</h2>
        <p>
          Scrile Inc. (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the SMeet platform (&ldquo;Service&rdquo;).
          This Privacy Policy explains how we collect, use, disclose, and protect your personal
          information when you use our Service.
        </p>
        <p>
          By using the Service, you agree to the collection and use of information in accordance
          with this policy. This policy should be read alongside our <Link to="/terms">Terms of Use</Link>.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>

        <h3>2.1 Information You Provide</h3>
        <ul>
          <li><strong>Account data:</strong> name, email address, username, password (stored as a hash)</li>
          <li><strong>Profile data:</strong> avatar image, display name</li>
          <li><strong>Messages:</strong> text messages, voice messages, and files you send through the Service</li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <ul>
          <li><strong>Device data:</strong> browser type, operating system, device identifiers</li>
          <li><strong>Usage data:</strong> features used, timestamps, interaction patterns</li>
          <li><strong>Network data:</strong> IP address, connection type (for WebRTC call quality)</li>
          <li><strong>Cookies:</strong> session cookies for authentication and preferences</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide, operate, and maintain the Service</li>
          <li>Deliver messages, files, and calls between users</li>
          <li>Authenticate your identity and secure your account</li>
          <li>Send essential notifications (account security, service updates)</li>
          <li>Improve performance and fix bugs</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal data to third parties.
          We do <strong>not</strong> use your message content for advertising or profiling.
        </p>
      </section>

      <section>
        <h2>4. Data Storage and Security</h2>
        <p>
          Your data is stored on secure servers. We employ industry-standard security measures including:
        </p>
        <ul>
          <li>TLS/SSL encryption for data in transit</li>
          <li>Encrypted storage for sensitive data at rest</li>
          <li>Access controls and audit logging</li>
          <li>Regular security assessments</li>
        </ul>
        <p>
          While we strive to protect your information, no system is 100% secure. You are responsible
          for keeping your account credentials confidential.
        </p>
      </section>

      <section>
        <h2>5. Data Sharing</h2>
        <p>We may share your information with:</p>
        <ul>
          <li><strong>Other users:</strong> your profile information and messages are visible to users you communicate with</li>
          <li><strong>Service providers:</strong> hosting and analytics providers that help us operate the Service, under strict data processing agreements</li>
          <li><strong>Law enforcement:</strong> when required by law, court order, or to protect safety</li>
        </ul>
        <p>
          We do not share your private messages with third parties for marketing or commercial purposes.
        </p>
      </section>

      <section>
        <h2>6. Video and Audio Calls</h2>
        <p>
          Video and audio calls use WebRTC technology with peer-to-peer connections where possible.
          Call signaling data passes through our servers, but media streams are transmitted directly
          between participants when network conditions allow.
        </p>
        <p>
          Call recordings (if enabled by participants) are stored securely and accessible only
          to authorized participants. Recordings are retained for 7 days unless otherwise configured.
        </p>
      </section>

      <section>
        <h2>7. Cookies</h2>
        <p>We use the following types of cookies:</p>
        <ul>
          <li><strong>Essential cookies:</strong> required for authentication and core functionality</li>
          <li><strong>Preference cookies:</strong> store your settings (theme, language)</li>
        </ul>
        <p>
          We do not use advertising or third-party tracking cookies.
          You can control cookies through your browser settings.
        </p>
      </section>

      <section>
        <h2>8. Data Retention</h2>
        <p>
          We retain your data for as long as your account is active or as needed to provide the Service.
          When you delete your account:
        </p>
        <ul>
          <li>Your profile data is removed within 30 days</li>
          <li>Messages you sent may remain visible to other participants</li>
          <li>Uploaded files are deleted from our storage within 30 days</li>
          <li>Some data may be retained for legal compliance or dispute resolution</li>
        </ul>
      </section>

      <section>
        <h2>9. Your Rights</h2>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul>
          <li><strong>Access</strong> the personal data we hold about you</li>
          <li><strong>Correct</strong> inaccurate or incomplete data</li>
          <li><strong>Delete</strong> your account and associated data</li>
          <li><strong>Export</strong> your data in a portable format</li>
          <li><strong>Object</strong> to certain types of data processing</li>
          <li><strong>Withdraw consent</strong> where processing is based on consent</li>
        </ul>
        <p>
          To exercise these rights, contact us at support@scrile.com. We will respond within 30 days.
        </p>
      </section>

      <section>
        <h2>10. International Data Transfers</h2>
        <p>
          Your data may be processed in countries other than your country of residence.
          We ensure appropriate safeguards are in place for international transfers,
          including standard contractual clauses where applicable.
        </p>
      </section>

      <section>
        <h2>11. Children&apos;s Privacy</h2>
        <p>
          The Service is not intended for users under 18 years of age. We do not knowingly
          collect personal information from children. If we discover that a child has provided
          us with personal data, we will delete it promptly.
        </p>
      </section>

      <section>
        <h2>12. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material
          changes by posting the updated policy on this page with a revised &ldquo;Last updated&rdquo; date.
          We encourage you to review this policy periodically.
        </p>
      </section>

      <section>
        <h2>13. Contact Us</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices, contact us at:
        </p>
        <p>
          <strong>Scrile Inc.</strong><br />
          Email: support@scrile.com<br />
          Website: <a href="https://www.scrile.com" target="_blank" rel="noreferrer">www.scrile.com</a>
        </p>
      </section>
    </>
  ),
};

const ru: LegalContent = {
  title: "Политика конфиденциальности",
  lastUpdated: "Обновлено: 10 апреля 2026",
  body: (
    <>
      <section>
        <h2>1. Введение</h2>
        <p>
          Scrile Inc. (далее «Компания», «мы») управляет платформой SMeet (далее «Сервис»).
          Настоящая Политика конфиденциальности описывает, как мы собираем, используем, раскрываем
          и защищаем ваши персональные данные при использовании Сервиса.
        </p>
        <p>
          Используя Сервис, вы соглашаетесь со сбором и использованием информации в соответствии с
          настоящей политикой. Эту политику следует читать вместе с нашими <Link to="/terms">Условиями использования</Link>.
        </p>
      </section>

      <section>
        <h2>2. Какие данные мы собираем</h2>

        <h3>2.1 Данные, которые вы предоставляете</h3>
        <ul>
          <li><strong>Данные аккаунта:</strong> имя, email, имя пользователя, пароль (хранится в виде хэша)</li>
          <li><strong>Данные профиля:</strong> аватар, отображаемое имя</li>
          <li><strong>Сообщения:</strong> текстовые и голосовые сообщения, файлы, которые вы отправляете через Сервис</li>
        </ul>

        <h3>2.2 Данные, собираемые автоматически</h3>
        <ul>
          <li><strong>Данные устройства:</strong> тип браузера, операционная система, идентификаторы устройства</li>
          <li><strong>Данные использования:</strong> используемые функции, временные метки, паттерны взаимодействия</li>
          <li><strong>Сетевые данные:</strong> IP-адрес, тип подключения (для качества WebRTC-звонков)</li>
          <li><strong>Cookies:</strong> сессионные cookies для аутентификации и настроек</li>
        </ul>
      </section>

      <section>
        <h2>3. Как мы используем ваши данные</h2>
        <p>Мы используем собранную информацию, чтобы:</p>
        <ul>
          <li>предоставлять, поддерживать и обслуживать Сервис;</li>
          <li>доставлять сообщения, файлы и звонки между пользователями;</li>
          <li>подтверждать вашу личность и защищать ваш аккаунт;</li>
          <li>отправлять важные уведомления (безопасность аккаунта, обновления сервиса);</li>
          <li>улучшать производительность и исправлять ошибки;</li>
          <li>соблюдать требования законодательства.</li>
        </ul>
        <p>
          Мы <strong>не</strong> продаём ваши персональные данные третьим лицам.
          Мы <strong>не</strong> используем содержание ваших сообщений для рекламы или профилирования.
        </p>
      </section>

      <section>
        <h2>4. Хранение и безопасность данных</h2>
        <p>
          Ваши данные хранятся на защищённых серверах. Мы применяем отраслевые меры безопасности, включая:
        </p>
        <ul>
          <li>TLS/SSL-шифрование данных при передаче;</li>
          <li>шифрование чувствительных данных при хранении;</li>
          <li>контроль доступа и журналирование действий;</li>
          <li>регулярные проверки безопасности.</li>
        </ul>
        <p>
          Несмотря на наши усилия, ни одна система не защищена на 100%. Вы несёте ответственность
          за конфиденциальность своих учётных данных.
        </p>
      </section>

      <section>
        <h2>5. Передача данных</h2>
        <p>Мы можем передавать вашу информацию следующим сторонам:</p>
        <ul>
          <li><strong>Другие пользователи:</strong> ваш профиль и сообщения видны пользователям, с которыми вы общаетесь.</li>
          <li><strong>Поставщики услуг:</strong> хостинг- и аналитические сервисы, помогающие нам обслуживать Сервис, по строгим соглашениям об обработке данных.</li>
          <li><strong>Правоохранительные органы:</strong> когда этого требует закон, судебное решение или защита безопасности.</li>
        </ul>
        <p>
          Мы не передаём ваши приватные сообщения третьим лицам в маркетинговых или коммерческих целях.
        </p>
      </section>

      <section>
        <h2>6. Видео- и аудиозвонки</h2>
        <p>
          Видео- и аудиозвонки используют технологию WebRTC с peer-to-peer соединениями, где это возможно.
          Сигнальные данные звонка проходят через наши серверы, а медиа-потоки передаются напрямую между
          участниками, когда позволяют сетевые условия.
        </p>
        <p>
          Записи звонков (если они включены участниками) хранятся в защищённом виде и доступны только
          уполномоченным участникам. По умолчанию записи хранятся 7 дней, если не настроено иное.
        </p>
      </section>

      <section>
        <h2>7. Cookies</h2>
        <p>Мы используем следующие типы cookies:</p>
        <ul>
          <li><strong>Технические cookies:</strong> необходимы для аутентификации и базовой работы.</li>
          <li><strong>Cookies настроек:</strong> сохраняют ваши предпочтения (тема, язык).</li>
        </ul>
        <p>
          Мы не используем рекламные или сторонние трекинговые cookies.
          Управление cookies доступно через настройки вашего браузера.
        </p>
      </section>

      <section>
        <h2>8. Срок хранения данных</h2>
        <p>
          Мы храним ваши данные, пока ваш аккаунт активен или пока это необходимо для предоставления Сервиса.
          При удалении вашего аккаунта:
        </p>
        <ul>
          <li>данные профиля удаляются в течение 30 дней;</li>
          <li>отправленные вами сообщения могут оставаться видимыми другим участникам;</li>
          <li>загруженные файлы удаляются из нашего хранилища в течение 30 дней;</li>
          <li>часть данных может сохраняться для соблюдения законодательства или разрешения споров.</li>
        </ul>
      </section>

      <section>
        <h2>9. Ваши права</h2>
        <p>В зависимости от юрисдикции вы можете иметь право:</p>
        <ul>
          <li><strong>Получить доступ</strong> к персональным данным, которые мы храним о вас.</li>
          <li><strong>Исправить</strong> неточные или неполные данные.</li>
          <li><strong>Удалить</strong> ваш аккаунт и связанные с ним данные.</li>
          <li><strong>Экспортировать</strong> ваши данные в переносимом формате.</li>
          <li><strong>Возражать</strong> против определённых типов обработки данных.</li>
          <li><strong>Отозвать согласие</strong>, если обработка основана на согласии.</li>
        </ul>
        <p>
          Чтобы реализовать эти права, напишите нам на support@scrile.com. Мы ответим в течение 30 дней.
        </p>
      </section>

      <section>
        <h2>10. Международная передача данных</h2>
        <p>
          Ваши данные могут обрабатываться в странах, отличных от страны вашего проживания.
          Мы обеспечиваем соответствующие гарантии при международной передаче, включая стандартные
          договорные условия, где это применимо.
        </p>
      </section>

      <section>
        <h2>11. Конфиденциальность детей</h2>
        <p>
          Сервис не предназначен для пользователей младше 18 лет. Мы намеренно не собираем
          персональные данные у детей. Если мы обнаружим, что ребёнок предоставил нам персональные
          данные, мы оперативно их удалим.
        </p>
      </section>

      <section>
        <h2>12. Изменения политики</h2>
        <p>
          Мы можем периодически обновлять данную Политику конфиденциальности. О существенных
          изменениях мы уведомим, разместив обновлённую версию на этой странице с новой датой
          «Обновлено». Рекомендуем периодически просматривать политику.
        </p>
      </section>

      <section>
        <h2>13. Контакты</h2>
        <p>
          Если у вас есть вопросы по данной Политике конфиденциальности или нашим практикам
          обработки данных, свяжитесь с нами:
        </p>
        <p>
          <strong>Scrile Inc.</strong><br />
          Email: support@scrile.com<br />
          Сайт: <a href="https://www.scrile.com" target="_blank" rel="noreferrer">www.scrile.com</a>
        </p>
      </section>
    </>
  ),
};

const catalogue: Record<AppLanguage, LegalContent> = { ru, en };

export function getPrivacyContent(lang: AppLanguage): LegalContent {
  return catalogue[lang] ?? catalogue.en;
}
